'use client';

import { useTranslations } from 'next-intl';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { resolveApiErrorToastMessage } from '../lib/api/localizeApiError';
import {
  addProjectMember,
  fetchProjectById,
  fetchProjectMembers,
  fetchTeamMembers,
  removeProjectMember,
  type ProjectMember,
  type TeamMember,
  type TeamRole,
} from '../lib/api';
import {
  fetchProjectPermissionCatalog,
  fetchProjectRoles,
  type ProjectPermissionCatalogItem,
  type ProjectRole,
} from '../lib/api/projectPermissions';
import { cn } from '../lib/cn';
import { getAuthSession } from '../lib/authSession';
import { useProjectPermissions } from '../hooks/useProjectPermissions';
import { useProjectPermissionLabels } from '../hooks/useProjectPermissionLabels';
import { MemberListItemSkeleton } from './MemberListItemSkeleton';
import { ProjectMemberRolesModal } from './ProjectMemberRolesModal';
import { ProjectRolesSection } from './ProjectRolesSection';
import { Select } from './Select';
import DeleteConfirmModal from './DeleteConfirmModal';
import { Spinner } from './loading/Spinner';

type ProjectMembersViewProps = {
  projectId: string;
};

/** 신규 역할을 구버전 클라이언트 호환용 `ProjectMember.role` 미러 값으로 변환한다. */
const projectRoleToLegacyRole = (role: ProjectRole): TeamRole => {
  if (role.builtInKey === 'PROJECT_OWNER') return 'OWNER';
  if (role.builtInKey === 'PROJECT_ADMIN') return 'ADMIN';
  return 'MEMBER';
};

export default function ProjectMembersView({ projectId }: ProjectMembersViewProps) {
  const t = useTranslations('projects.members');
  const tRoles = useTranslations('projects.roles');
  const tCommon = useTranslations('common');
  const tRoot = useTranslations();
  const tRootRef = useRef(tRoot);
  tRootRef.current = tRoot;
  const session = useMemo(() => getAuthSession()!, []);

  const [members, setMembers] = useState<ProjectMember[]>([]);
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [addEmail, setAddEmail] = useState('');
  const [addProjectRoleId, setAddProjectRoleId] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isAdding, setIsAdding] = useState(false);
  const [removingMemberId, setRemovingMemberId] = useState<string | null>(null);
  const [confirmRemoveMemberId, setConfirmRemoveMemberId] = useState<string | null>(null);
  const [projectRoles, setProjectRoles] = useState<ProjectRole[]>([]);
  const [permissionCatalog, setPermissionCatalog] = useState<ProjectPermissionCatalogItem[]>([]);
  const [isRolesLoading, setIsRolesLoading] = useState(true);
  const [hasRolesLoadFailed, setHasRolesLoadFailed] = useState(false);
  const [assignRolesMemberId, setAssignRolesMemberId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'members' | 'roles'>('members');

  // 판정 기준은 역할 문자열이 아니라 permission이다. OWNER 부여·회수만 여전히 소유자 축이라
  // 서버 정책(프로젝트 OWNER 또는 팀 OWNER)과 같은 조건을 화면에서도 그대로 쓴다.
  const { can, roles: myRoles } = useProjectPermissions({ projectId });
  const { roleName } = useProjectPermissionLabels();
  const canManage = can('members.manage');
  const canManageRoles = can('roles.manage');
  const isTeamOwner = useMemo(
    () => teamMembers.some((member) => member.memberId === session.member.id && member.role === 'OWNER'),
    [teamMembers, session.member.id],
  );
  const canManageOwner = myRoles.some((role) => role.builtInKey === 'PROJECT_OWNER') || isTeamOwner;
  const addRoleOptions = useMemo(
    () =>
      projectRoles.filter(
        (role) => role.archivedAt === null && (role.builtInKey !== 'PROJECT_OWNER' || canManageOwner),
      ),
    [projectRoles, canManageOwner],
  );
  const defaultAddProjectRoleId = addRoleOptions.find((role) => role.builtInKey === 'PROJECT_MEMBER')?.id ?? '';
  const selectedAddProjectRole = addRoleOptions.find((role) => role.id === addProjectRoleId) ?? null;
  // 고른 역할이 없으면 공용 Select가 빈 라벨을 그린다(`selectedOption?.label ?? value`).
  // 무엇을 골라야 하는지 알려주되 다시 고를 수는 없도록 비활성 자리표시자를 앞에 둔다.
  const addRoleSelectOptions = useMemo(() => {
    const options = addRoleOptions.map((role) => ({ value: role.id, label: roleName(role) }));
    return selectedAddProjectRole ? options : [{ value: '', label: t('rolePlaceholder'), disabled: true }, ...options];
  }, [addRoleOptions, roleName, selectedAddProjectRole, t]);

  const reloadMembers = useCallback(async () => {
    setIsLoading(true);
    try {
      const project = await fetchProjectById(session.token, projectId);
      const [projectMembers, foundTeamMembers] = await Promise.all([
        fetchProjectMembers(session.token, projectId),
        fetchTeamMembers(session.token, project.teamId),
      ]);
      setMembers(projectMembers);
      setTeamMembers(foundTeamMembers);
    } catch (loadError) {
      const message = resolveApiErrorToastMessage(tRootRef.current, loadError);
      if (message) toast.error(message);
    } finally {
      setIsLoading(false);
    }
  }, [projectId, session.token]);

  useEffect(() => {
    if (!projectId) {
      toast.error(t('toastProjectIdMissing'));
      setIsLoading(false);
      return;
    }

    void reloadMembers();
  }, [projectId, reloadMembers, t]);

  /**
   * 역할 목록과 permission 카탈로그는 권한 축이 다르다(`members.manage` / `roles.manage`).
   * 없는 권한으로 요청하면 403이 정상이므로 아예 부르지 않는다.
   */
  const reloadRoles = useCallback(async () => {
    if (!projectId || (!canManage && !canManageRoles)) {
      setIsRolesLoading(false);
      return;
    }
    setIsRolesLoading(true);
    try {
      const [roles, catalog] = await Promise.all([
        fetchProjectRoles(session.token, projectId),
        canManageRoles ? fetchProjectPermissionCatalog(session.token, projectId) : Promise.resolve([]),
      ]);
      setProjectRoles(roles);
      setPermissionCatalog(catalog);
      setHasRolesLoadFailed(false);
    } catch (loadError) {
      // 역할 목록이 없으면 추가 폼 전체가 잠긴다. 토스트만 띄우면 사용자가 다시 시도할 방법이 없으므로
      // 실패 상태를 남겨 폼 옆에 재시도 수단을 붙인다.
      setHasRolesLoadFailed(true);
      const message = resolveApiErrorToastMessage(tRootRef.current, loadError);
      if (message) toast.error(message);
    } finally {
      setIsRolesLoading(false);
    }
  }, [projectId, session.token, canManage, canManageRoles]);

  useEffect(() => {
    void reloadRoles();
  }, [reloadRoles]);

  useEffect(() => {
    if (isRolesLoading) return;
    setAddProjectRoleId((current) => {
      if (current && addRoleOptions.some((role) => role.id === current)) return current;
      return defaultAddProjectRoleId;
    });
  }, [addRoleOptions, defaultAddProjectRoleId, isRolesLoading]);

  const handleAdd = async () => {
    if (!canManage) {
      toast.error(t('toastPermissionManage'));
      return;
    }
    if (!addEmail.trim()) return;
    if (!selectedAddProjectRole) return;

    setIsAdding(true);
    try {
      const result = await addProjectMember(session.token, projectId, {
        email: addEmail.trim(),
        role: projectRoleToLegacyRole(selectedAddProjectRole),
        projectRoleId: selectedAddProjectRole.id,
      });
      if ('invited' in result) {
        toast.success(t('toastInviteSent'));
        setAddEmail('');
        setAddProjectRoleId(defaultAddProjectRoleId);
        return;
      }
      await reloadMembers();
      setAddEmail('');
      setAddProjectRoleId(defaultAddProjectRoleId);
    } catch (submitError) {
      const message = resolveApiErrorToastMessage(tRoot, submitError);
      if (message) toast.error(message);
    } finally {
      setIsAdding(false);
    }
  };

  const handleRemove = async (memberId: string) => {
    if (!canManage) {
      toast.error(t('toastPermissionRemove'));
      return;
    }
    const target = members.find((m) => m.memberId === memberId);
    if (!target) return;
    if (target.role === 'OWNER') {
      toast.error(t('toastOwnerCannotRemove'));
      return;
    }
    setConfirmRemoveMemberId(memberId);
  };

  const handleConfirmRemove = async () => {
    if (!confirmRemoveMemberId) return;
    const target = members.find((m) => m.memberId === confirmRemoveMemberId);
    if (!target) {
      setConfirmRemoveMemberId(null);
      return;
    }

    setRemovingMemberId(confirmRemoveMemberId);
    try {
      await removeProjectMember(session.token, projectId, confirmRemoveMemberId);
      setMembers((prev) => prev.filter((m) => m.memberId !== confirmRemoveMemberId));
      setConfirmRemoveMemberId(null);
    } catch (removeError) {
      const message = resolveApiErrorToastMessage(tRoot, removeError);
      if (message) toast.error(message);
    } finally {
      setRemovingMemberId(null);
    }
  };

  const assignRolesTarget = assignRolesMemberId
    ? (members.find((member) => member.memberId === assignRolesMemberId) ?? null)
    : null;

  const confirmRemoveTarget = confirmRemoveMemberId
    ? members.find((member) => member.memberId === confirmRemoveMemberId)
    : null;
  const confirmRemoveName = confirmRemoveTarget?.member.email ?? confirmRemoveMemberId ?? '-';

  const membersPanel = (
    <>
      {!isLoading && !canManage ? <p className="-mt-1 mb-4 text-xs text-warning">{t('permissionHint')}</p> : null}

      <div className="mb-4 rounded-2xl border border-base-300 bg-base-100 p-4">
        <div className="flex flex-col gap-3 md:flex-row md:items-end">
          <div className="form-control flex-1">
            <label className="mb-1.5 text-xs font-medium text-[var(--color-fg-subtle)]" htmlFor="pmv-add-email">
              {t('email')}
            </label>
            <input
              id="pmv-add-email"
              type="email"
              className="input input-bordered input-sm w-full"
              placeholder={t('emailPlaceholder')}
              value={addEmail}
              onChange={(event) => setAddEmail(event.target.value)}
            />
          </div>
          <div className="form-control">
            <label className="mb-1.5 text-xs font-medium text-[var(--color-fg-subtle)]" htmlFor="pmv-add-role">
              {t('role')}
            </label>
            <Select
              id="pmv-add-role"
              size="sm"
              value={addProjectRoleId}
              onChange={setAddProjectRoleId}
              disabled={!canManage || isLoading || isRolesLoading || addRoleOptions.length === 0}
              options={addRoleSelectOptions}
            />
          </div>
          <button
            type="button"
            className="btn btn-primary btn-sm"
            disabled={!canManage || !projectId || !addEmail.trim() || !selectedAddProjectRole || isAdding || isLoading}
            onClick={() => void handleAdd()}
          >
            {isAdding ? t('adding') : t('addMember')}
          </button>
        </div>

        {/* 역할 조회가 한 번 실패하면 폼이 통째로 잠기므로, 왜 잠겼는지와 되살릴 방법을 같은 자리에 둔다. */}
        {canManage && !isRolesLoading && hasRolesLoadFailed ? (
          <p className="mt-2 flex flex-wrap items-center gap-2 text-xs text-warning">
            {t('roleLoadFailed')}
            <button type="button" className="btn btn-ghost btn-xs" onClick={() => void reloadRoles()}>
              {tCommon('actions.retry')}
            </button>
          </p>
        ) : canManage && !isRolesLoading && !selectedAddProjectRole ? (
          <p className="mt-2 text-xs text-warning">{t('roleNoneAvailable')}</p>
        ) : null}
      </div>

      {isLoading ? (
        <MemberListItemSkeleton />
      ) : members.length === 0 ? (
        <div className="rounded-xl border border-dashed border-base-300 p-8 text-center">
          <h3 className="mb-1 font-semibold text-base-content">{t('emptyTitle')}</h3>
          <p className="text-sm text-[var(--color-fg-muted)]">{t('emptyDescription')}</p>
        </div>
      ) : (
        <ul className="space-y-2">
          {members.map((member) => (
            <li
              key={member.id}
              className="flex min-w-0 items-start justify-between gap-3 rounded-xl border border-base-300 bg-base-100 p-4"
            >
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-medium">{member.member.email}</div>
                <div className="truncate text-xs text-[var(--color-fg-subtle)]">{member.member.nickname}</div>
                {/*
                 * 권한 축은 역할 하나다. 예전에는 legacy `ProjectMember.role` 셀렉트가 이 자리에
                 * 같이 있었는데, 서버는 "역할 할당이 있으면 legacy는 무시"인 반면 화면은 셀렉트를
                 * 더 크게 보여줘서 두 컨트롤이 서로를 덮어쓰는 것처럼 읽혔다.
                 */}
                <div className="mt-1.5 flex flex-wrap items-center gap-1">
                  {(member.roles ?? []).length === 0 ? (
                    <span className="text-[11px] text-[var(--color-fg-muted)]">{t('noRoles')}</span>
                  ) : (
                    (member.roles ?? []).map((role) => (
                      <span
                        key={role.id}
                        className="rounded-full px-2 py-0.5 text-[11px]"
                        style={{
                          background: role.builtInKey === null ? 'var(--chip-neutral-bg)' : 'var(--chip-primary-bg)',
                          color: role.builtInKey === null ? 'var(--chip-neutral-fg)' : 'var(--chip-primary-fg)',
                        }}
                      >
                        {roleName(role)}
                      </span>
                    ))
                  )}
                </div>
              </div>
              <div className="flex shrink-0 items-center gap-2">
                {canManage || canManageRoles ? (
                  <button
                    type="button"
                    className="btn btn-ghost btn-xs"
                    disabled={member.role === 'OWNER' && !canManageOwner}
                    onClick={() => setAssignRolesMemberId(member.memberId)}
                  >
                    {tRoles('assignRoles')}
                  </button>
                ) : null}
                <button
                  type="button"
                  aria-label={t('remove')}
                  title={t('remove')}
                  className="btn btn-ghost btn-xs text-error hover:bg-error/10"
                  disabled={!canManage || member.role === 'OWNER' || removingMemberId === member.memberId}
                  onClick={() => void handleRemove(member.memberId)}
                >
                  {removingMemberId === member.memberId ? (
                    <Spinner />
                  ) : (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-4 w-4"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="1.5"
                        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3m-4 0h14"
                      />
                    </svg>
                  )}
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </>
  );

  return (
    <div>
      {/*
       * 역할 카탈로그는 멤버 목록 아래 곁다리로 붙이지 않고 같은 자리에서 전환한다. 모달로 띄우면
       * 이 화면이 이미 워크스페이스 모달 > 프로젝트 설정 모달 안이라 dialog가 3~4겹이 된다.
       * 역할을 관리할 수 없는 사람에게는 전환 자체가 없으므로 세그먼트를 그리지 않는다.
       */}
      {canManageRoles ? (
        <div
          role="tablist"
          aria-label={t('segmentLabel')}
          className="mb-4 grid w-full max-w-xs grid-cols-2 rounded-xl bg-base-200 p-1"
        >
          {(['members', 'roles'] as const).map((tab) => (
            <button
              key={tab}
              id={`pmv-tab-${tab}`}
              type="button"
              role="tab"
              aria-selected={activeTab === tab}
              aria-controls={`pmv-tabpanel-${tab}`}
              className={cn(
                'btn btn-ghost btn-sm min-h-9 rounded-lg border-0 text-xs font-medium sm:text-sm',
                activeTab === tab && 'bg-base-100 text-primary shadow-xs',
              )}
              onClick={() => setActiveTab(tab)}
            >
              {tab === 'members' ? t('segmentMembers') : t('segmentRoles')}
            </button>
          ))}
        </div>
      ) : null}

      {activeTab === 'roles' ? (
        <div id="pmv-tabpanel-roles" role="tabpanel" aria-labelledby="pmv-tab-roles">
          <ProjectRolesSection
            projectId={projectId}
            token={session.token}
            roles={projectRoles}
            catalog={permissionCatalog}
            isLoading={isRolesLoading}
            canManageRoles={canManageRoles}
            onChanged={reloadRoles}
          />
        </div>
      ) : (
        <div id="pmv-tabpanel-members" role="tabpanel" aria-labelledby="pmv-tab-members">
          {membersPanel}
        </div>
      )}

      {assignRolesTarget ? (
        <ProjectMemberRolesModal
          isOpen
          projectId={projectId}
          token={session.token}
          memberId={assignRolesTarget.memberId}
          memberLabel={assignRolesTarget.member.email}
          roles={projectRoles}
          catalog={permissionCatalog}
          assignedRoleIds={(assignRolesTarget.roles ?? []).map((role) => role.id)}
          canManageMembers={canManage}
          canManageRoles={canManageRoles}
          onClose={() => setAssignRolesMemberId(null)}
          onSaved={async () => {
            await Promise.all([reloadRoles(), reloadMembers()]);
          }}
        />
      ) : null}

      <DeleteConfirmModal
        isOpen={confirmRemoveMemberId !== null}
        title={t('remove')}
        message={t('confirmRemove', { name: confirmRemoveName })}
        confirmLabel={t('remove')}
        cancelLabel={tCommon('actions.cancel')}
        isPending={removingMemberId === confirmRemoveMemberId}
        onConfirm={() => void handleConfirmRemove()}
        onCancel={() => {
          if (removingMemberId === null) {
            setConfirmRemoveMemberId(null);
          }
        }}
      />
    </div>
  );
}
